import { useCallback, useMemo } from "react";

import { faArrowUpRight, faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";

import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { CaseStatusPillWithSchema } from "src/cases/common/CaseStatusPillWithSchema";
import { Icon, IconButton } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { getBaseUrl, getUrlToCaseViewPage } from "src/router/urls";

export type BulkActionErrorCaseData = {
  id: string;
  status: string;
  sub_status?: string;
  templateId: string;
  versionId: string;
};

export enum BulkActionErrorType {
  INCOMPLETE_TASKS = "incomplete_tasks",
  CONFLICT = "conflict",
}

type BulkActionErrorModalProps = {
  isOpen: boolean;
  errorType: BulkActionErrorType;
  cases: BulkActionErrorCaseData[];
  wsId: string;
  orgId: string;
  onClose: () => void;
};

const columnHelper = createColumnHelper<BulkActionErrorCaseData>();

const ERROR_CONFIG = {
  [BulkActionErrorType.INCOMPLETE_TASKS]: {
    title: "Cases with incomplete required tasks",
    description:
      "Complete the required tasks for these cases to apply the action.",
  },
  [BulkActionErrorType.CONFLICT]: {
    title: "Cases with an action in progress",
    description:
      "These cases already have an action running. You can't start another action until the current one is finished.",
  },
};

export const BulkActionErrorModal: React.FC<BulkActionErrorModalProps> = ({
  isOpen,
  errorType,
  cases,
  wsId,
  orgId,
  onClose,
}) => {
  const openCaseInNewTab = useCallback(
    (caseId: string) => {
      const fullUrl = getBaseUrl() + getUrlToCaseViewPage(orgId, wsId, caseId);
      window.open(fullUrl, "_blank");
    },
    [orgId, wsId],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("id", {
        header: (info) => (
          <Header dataLoc="bulk-action-error-header-id" info={info}>
            <span className="whitespace-nowrap">Case ID</span>
          </Header>
        ),
        cell: (info) => (
          <Cell className="w-24" info={info}>
            <TruncatedIdPill id={info.getValue()} />
          </Cell>
        ),
      }),
      columnHelper.accessor("status", {
        header: (info) => (
          <Header dataLoc="bulk-action-error-header-status" info={info}>
            <div className="flex items-center gap-x-0.5">
              <Icon icon={faSpinner} size="2xs" />
              Status
            </div>
          </Header>
        ),
        cell: (info) => {
          const caseData = info.row.original;

          return (
            <Cell
              dataLoc={`bulk-action-error-cell-status-${info.row.id}`}
              info={info}
            >
              <CaseStatusPillWithSchema
                templateId={caseData.templateId}
                versionId={caseData.versionId}
                status={caseData.status}
                subStatus={caseData.sub_status}
              />
            </Cell>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        cell: ({ row }) => (
          <IconButton
            icon={faArrowUpRight}
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              openCaseInNewTab(row.original.id);
            }}
          />
        ),
        size: 50,
      }),
    ],
    [openCaseInNewTab],
  );

  const config = ERROR_CONFIG[errorType];

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header description={config.description}>
        {config.title}
      </Modal.Header>
      <Modal.Content>
        <Table columns={columns} data={cases} variant="compact" />
      </Modal.Content>
      <Modal.Footer primaryButton={null} secondaryButton="Close" />
    </Modal>
  );
};
